/**
 * i18next-Setup. DE/EN. Wird in main.tsx vor App-Mount initialisiert.
 *
 * Locale-Resolution-Reihenfolge:
 *  1) Explizit via ?lang=de Query-Param (Magic-Link-Mails koennen das setzen)
 *  2) sessionStorage('stayon.lang') — User-Wahl im Portal sticky
 *  3) navigator.language → de/en fallback
 *
 * Default-Namespace 'guest' enthaelt alle Guest-Portal-Strings. Admin-Strings kommen
 * nach Bedarf in 'admin' Namespace (Backlog).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import de from './locales/de.json';
import en from './locales/en.json';
import { pluginGerman, pluginEnglish } from './pluginTranslations';
import { automationGerman, automationEnglish } from './automationTranslations';
import { pluginCaseGerman, pluginCaseEnglish } from './pluginCaseTranslations';
import { workspaceGerman, workspaceEnglish } from './workspaceTranslations';
import { accountRetentionGerman, accountRetentionEnglish } from './accountRetentionTranslations';

export const SUPPORTED_LOCALES = ['de', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
const STORAGE_KEY = 'stayon.lang';

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'de';
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('lang');
  if (fromQuery && (SUPPORTED_LOCALES as readonly string[]).includes(fromQuery)) {
    return fromQuery as Locale;
  }
  const stored = window.sessionStorage.getItem(STORAGE_KEY);
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as Locale;
  }
  const nav = navigator.language?.slice(0, 2).toLowerCase();
  return nav === 'de' ? 'de' : 'en';
}

export const initialLocale = detectInitialLocale();

void i18n.use(initReactI18next).init({
  resources: {
    de: {
      guest: de,
      plugins: pluginGerman,
      automation: automationGerman,
      pluginCases: pluginCaseGerman,
      workspaces: workspaceGerman,
      accountRetention: accountRetentionGerman,
    },
    en: {
      guest: en,
      plugins: pluginEnglish,
      automation: automationEnglish,
      pluginCases: pluginCaseEnglish,
      workspaces: workspaceEnglish,
      accountRetention: accountRetentionEnglish,
    },
  },
  lng: initialLocale,
  fallbackLng: 'de',
  defaultNS: 'guest',
  interpolation: { escapeValue: false },
});

export function setLocale(locale: Locale) {
  window.sessionStorage.setItem(STORAGE_KEY, locale);
  void i18n.changeLanguage(locale);
}

export default i18n;
